import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import { LoadingSpinner } from '@shared/components/LoadingSpinner';
import { useSnackbar } from '@shared/services/SnackbarService/SnackbarServiceContext';

import NetworkQuestionCard from './components/NetworkQuestionCard';
import { useNetworkQuestionsContext } from './NetworkQuestionsContext';

import './NetworkQuestionsPage.scss';

export default function NetworkQuestionsPage(): React.ReactElement {
  const { questions, loading, submitAnswer } = useNetworkQuestionsContext();
  const { showSnackbar } = useSnackbar();

  const handleSubmit = async (questionId: number, answer: string): Promise<boolean> => {
    const ok = await submitAnswer(questionId, answer);
    if (ok) {
      showSnackbar('Answer sent — thanks for the help', 'success', 4000);
    } else {
      showSnackbar('Could not send answer — please try again', 'error', 4000);
    }
    return ok;
  };

  return (
    <div className="network-questions-page">
      <header className="network-questions-page__header">
        <p className="network-questions-page__eyebrow">For the network</p>
        <div className="network-questions-page__hero-row">
          <div className="network-questions-page__hero-icon">
            <HelpOutlineIcon fontSize="medium" />
          </div>
          <div className="network-questions-page__hero-copy">
            <h1 className="network-questions-page__title">Questions for you</h1>
            <p className="network-questions-page__subtitle">
              Account Executives are working accounts you know first-hand. Share what you've learned — your answer goes
              straight back to the AE running the deal.
            </p>
          </div>
        </div>
      </header>

      <aside className="network-questions-page__paid-pitch">
        <div className="network-questions-page__paid-icon">
          <PaidOutlinedIcon fontSize="small" />
        </div>
        <div className="network-questions-page__paid-copy">
          <p className="network-questions-page__paid-title">Helpful answers lead to paid intelligence requests</p>
          <p className="network-questions-page__paid-body">
            When an AE finds your answer valuable, they can follow up with a paid private intelligence request — sent
            directly to you. The more substantive your answers, the more likely you are to be tapped for a deeper
            conversation.
          </p>
        </div>
      </aside>

      {loading ? (
        <div className="network-questions-page__loading">
          <LoadingSpinner />
        </div>
      ) : questions.length === 0 ? (
        <div className="network-questions-page__empty">
          <HelpOutlineIcon className="network-questions-page__empty-icon" />
          <h2 className="network-questions-page__empty-title">Nothing to answer right now</h2>
          <p className="network-questions-page__empty-body">
            We'll surface questions on companies you've reviewed or worked with. The more deal reviews and deal insights
            you share, the more matches you'll see here.
          </p>
        </div>
      ) : (
        <div className="network-questions-page__list">
          {questions.map((question) => (
            <NetworkQuestionCard key={question.id} question={question} onSubmit={handleSubmit} />
          ))}
        </div>
      )}
    </div>
  );
}
